import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import {
  ActionsDropdown,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { TextPill } from 'components/FbUI/TextPill'
import { FbModal } from 'components/UI/Modal'
import { useAuth } from 'context/authentication/useAuth'
import { InviteUserModal } from 'features/users/InviteUserModal'
import { useRSMEligibleUsers } from 'hooks/useRSMEligibleUsers'
import { User, USER_TYPE_LIMITED } from 'models/user'
import Select from 'components/FormUtils/Select'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { AlertTriangle, Info } from 'lucide-react'
import * as S from './styles'

interface UserTableFormValues {
  user_type: string
  rsm_assignment: number | null
}

export function UserTable() {
  const api = useApiService()
  const { user, isAdmin } = useAuth()
  const { enableUserTypes } = useFeatureFlagService()
  const queryClient = useQueryClient()

  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { control, reset, watch } = useForm<UserTableFormValues>({
    defaultValues: { user_type: '', rsm_assignment: null },
  })

  const selectedType = watch('user_type')
  const selectedRSM = watch('rsm_assignment')

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      return await api.getUsers()
    },
  })

  const { data: userTypes } = useQuery({
    queryKey: ['user-types'],
    queryFn: async () => {
      return await api.getUserTypes()
    },
    enabled: enableUserTypes && isAdmin,
  })

  const { rsmOptions } = useRSMEligibleUsers(
    enableUserTypes && isAdmin,
    selectedUser?.id
  )

  const { data: companyData } = api.useGetCompanyDetails()

  const handleEdit = useCallback(
    (targetUser: User) => {
      setSelectedUser(targetUser)
      reset({
        user_type: targetUser.user_type ?? 'Standard',
        rsm_assignment: targetUser.rsm_assignment ?? null,
      })
    },
    [reset]
  )

  const isLimitedType = selectedType === USER_TYPE_LIMITED
  const isUpgrading =
    selectedUser?.user_type === USER_TYPE_LIMITED && !isLimitedType
  const isDowngrading =
    selectedUser?.user_type !== USER_TYPE_LIMITED && isLimitedType
  const companyBucket = companyData?.seat_buckets?.find((b) =>
    b.user_types.some((t) => t === 'Admin' || t === 'Standard')
  )
  const maxSeatsReached = companyBucket
    ? companyBucket.active >= companyBucket.limit
    : false

  const blockAccept = useMemo(() => {
    if (!selectedType) return true
    if (isLimitedType && !selectedRSM) return true
    if (isUpgrading && maxSeatsReached) return true
    // Block if nothing changed
    const sameType = selectedType === (selectedUser?.user_type ?? 'Standard')
    if (isLimitedType) {
      return sameType && selectedRSM === (selectedUser?.rsm_assignment ?? null)
    }
    return sameType
  }, [
    selectedType,
    isLimitedType,
    selectedRSM,
    isUpgrading,
    maxSeatsReached,
    selectedUser,
  ])

  const assignedBrokersCount = selectedUser?.assigned_brokers_count ?? 0

  const handleSubmit = useCallback(async () => {
    if (!selectedUser || !selectedType) return
    setIsSubmitting(true)
    try {
      await api.updateUserType(
        selectedUser.id,
        selectedType,
        isLimitedType ? selectedRSM : null
      )
      toast.success('User type updated successfully')
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['company-details'] })
      queryClient.invalidateQueries({ queryKey: ['rsm-eligible-users'] })
      setSelectedUser(null)
    } catch {
      toast.error('Failed to update user type')
    } finally {
      setIsSubmitting(false)
    }
  }, [
    selectedUser,
    selectedType,
    selectedRSM,
    isLimitedType,
    api,
    refetch,
    queryClient,
  ])

  const columns = useMemo<ColumnDef<User, any>[]>(() => {
    const cols: ColumnDef<User, any>[] = [
      {
        accessorKey: 'first_name',
        header: 'First Name',
        size: 200,
      },
      {
        accessorKey: 'last_name',
        header: 'Last Name',
        size: 200,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 150,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 150,
        cell: (info) => {
          if (info.getValue()) {
            return (
              <TextPill textColor="#13462C" backgroundColor="#D1E8D5">
                Active
              </TextPill>
            )
          } else {
            return (
              <TextPill textColor="#464646" backgroundColor="#E3E1DA">
                Inactive
              </TextPill>
            )
          }
        },
      },
    ]

    if (enableUserTypes) {
      cols.push({
        accessorKey: 'user_type',
        header: 'User Type',
        size: 120,
        cell: (info) => {
          const value = (info.getValue() as string) ?? 'Standard'
          return <span>{value}</span>
        },
      })

      cols.push({
        id: 'rsm_assignment',
        header: 'Sample Reviewer',
        size: 180,
        cell: ({ row }) => {
          const u = row.original
          if (u.user_type !== USER_TYPE_LIMITED) return '-'
          if (u.rsm_assignment_name) {
            return <span>{u.rsm_assignment_name}</span>
          }
          return (
            <span className="flex items-center gap-1 text-fg-secondary">
              <AlertTriangle className="h-4 w-4 text-warning" />
              Unassigned
            </span>
          )
        },
      })

      if (isAdmin) {
        cols.push({
          id: 'actions',
          header: 'Actions',
          size: 70,
          meta: {
            isEditable: false,
            headerDisableOrdering: true,
            rightAlign: true,
            isLastColumn: true,
          },
          cell: ({ row }) => {
            const targetUser = row.original
            const isSelf = targetUser.id === user?.id
            return (
              <ActionsDropdown
                className="w-full flex justify-end items-center"
                actions={[
                  {
                    ...createEditAction(
                      () => handleEdit(targetUser),
                      'Edit User Type'
                    ),
                    disabled: isSelf,
                    disabledReason: isSelf
                      ? 'You cannot change your own user type'
                      : undefined,
                  },
                ]}
              />
            )
          },
        })
      }
    }

    return cols
  }, [enableUserTypes, isAdmin, user?.id, handleEdit])

  if (isLoading || !data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          width: '100%',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  const descriptionText = `
  All ${user?.company.name} users who have created an account on First Bite. To add a team member, make sure to invite them using their work email.
  `
  return (
    <DataTableContainer>
      <ClientDataTable
        tableHeader={
          <S.SettingsTableHeader>
            <OverflownTextTooltip
              tooltipText={descriptionText}
              maxLines={4}
              placement="bottom"
            >
              <S.TableDescriptionText>{descriptionText}</S.TableDescriptionText>
            </OverflownTextTooltip>
            <InviteUserModal existingUsers={data} />
          </S.SettingsTableHeader>
        }
        height={'100%'}
        data={data}
        columns={columns}
      />

      <FbModal
        open={!!selectedUser}
        onOpenChange={(open) => {
          if (!open) setSelectedUser(null)
        }}
        title={
          selectedUser
            ? `Select what user type to change ${selectedUser.first_name} ${selectedUser.last_name}`
            : 'Edit User Type'
        }
        loading={isSubmitting}
        blockAccept={blockAccept}
        onAccept={handleSubmit}
        acceptButtonText="Update"
        size="md"
      >
        <div className="flex flex-col gap-4">
          <Select
            control={control}
            name="user_type"
            label="User Type"
            options={userTypes ?? []}
            isClearable={false}
            disabled={isSubmitting}
          />

          {isUpgrading &&
            (maxSeatsReached ? (
              <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <p className="font-medium text-red-800">
                  Max seats reached. Please contact First Bite to add additional
                  seats.
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-cyan-300 bg-cyan-50 p-3 text-sm">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-cyan-600" />
                <p className="text-cyan-800">
                  Upgrading this user will count as a seat. You are currently
                  using {companyBucket?.active ?? 0}/{companyBucket?.limit ?? 0}{' '}
                  seats.
                </p>
              </div>
            ))}

          {isDowngrading && (
            <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">
                    Downgrading this user will restrict access to most of First
                    Bite.
                  </p>
                  {assignedBrokersCount > 0 && (
                    <p className="mt-1.5 text-yellow-700">
                      {assignedBrokersCount} broker
                      {assignedBrokersCount > 1 ? 's' : ''} currently assigned
                      to this user as their sample reviewer will be unassigned
                      and blocked from submitting sample requests until
                      reassigned.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isLimitedType && (
            <Select
              control={control}
              name="rsm_assignment"
              label="Assigned Sample Reviewer"
              options={rsmOptions}
              isClearable={false}
              disabled={isSubmitting}
              placeholder="Select a sample reviewer..."
            />
          )}
        </div>
      </FbModal>
    </DataTableContainer>
  )
}
